/**
 * Phase 5: Query Rules
 * Index awareness, query limits, performance rules
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { 
  Zap, Loader2, Check, Plus, Trash2, AlertTriangle, 
  Clock, Database, Shield, RefreshCw
} from 'lucide-react';
import { getConnections } from '../../services/api';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const Phase5QueryRules = ({ onComplete, isAdmin }) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connections, setConnections] = useState([]);
  const [rules, setRules] = useState({
    global: {
      default_limit: 1000,
      max_limit: 10000,
      require_date_filter_days: 90,
      forbidden_operations: ['DELETE', 'UPDATE', 'DROP', 'TRUNCATE'],
      peak_hours: { start: 9, end: 18, timezone: 'UTC' },
      peak_hour_limit: 500
    },
    tables: []  // Table-specific rules
  });

  const [newTableRule, setNewTableRule] = useState({
    server: '',
    database: '',
    table: '',
    always_require_date_filter: false,
    date_column: '',
    max_rows_without_limit: 100,
    has_partitioning: false,
    partition_column: '',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const conns = await getConnections();
      setConnections(conns);
      
      // Fetch existing query rules
      let fetchedRules = null;
      try {
        const response = await axios.get(`${API}/api/learning/query-rules`, { headers: getAuthHeader() });
        if (response.data.rules) {
          fetchedRules = response.data.rules;
          setRules(response.data.rules);
        }
      } catch (e) {
        // Endpoint might not exist yet, use defaults
        console.log('Query rules endpoint not found, using defaults');
      }
      
      // Calculate progress using fetched rules (not stale state)
      if (fetchedRules) {
        const hasGlobalRules = fetchedRules.global?.default_limit > 0;
        const hasTableRules = fetchedRules.tables?.length > 0;
        onCompleteRef.current(hasGlobalRules ? 100 : 0);
      } else {
        onCompleteRef.current(0);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveGlobalRules = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/learning/query-rules`, { rules }, { headers: getAuthHeader() });
      toast.success('Query rules saved');
      // Report progress: global rules saved = 100% complete
    onCompleteRef.current(rules.tables?.length > 0 ? 100 : (hasGlobalRules ? 100 : 50));
    } catch (error) {
      toast.error('Failed to save query rules');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTableRule = () => {
    if (!newTableRule.table) {
      toast.error('Please select a table');
      return;
    }
    
    setRules(prev => ({
      ...prev,
      tables: [...(prev.tables || []), { ...newTableRule, id: Date.now() }]
    }));
    
    setNewTableRule({
      server: '',
      database: '',
      table: '',
      always_require_date_filter: false,
      date_column: '',
      max_rows_without_limit: 100,
      has_partitioning: false,
      partition_column: '',
      notes: ''
    });
    
    toast.success('Table rule added');
  };

  const handleRemoveTableRule = (id) => {
    setRules(prev => ({
      ...prev,
      tables: prev.tables.filter(t => t.id !== id)
    }));
  };

  if (loading) {
    return (
      <Card className="bg-white border-gray-200">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-gray-200">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-600" />
          Phase 5: Query Rules & Performance
        </CardTitle>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-gray-500">
          Define query safety rules to prevent the AI from running expensive queries that could slow down your database.
        </p>

        {/* Global Rules */}
        <div className="p-4 border rounded-lg space-y-4 bg-gray-50">
          <h4 className="font-medium text-gray-700 flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-500" />
            Global Query Rules
          </h4>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">Default LIMIT</Label>
              <Input
                type="number"
                value={rules.global.default_limit}
                onChange={e => setRules({...rules, global: {...rules.global, default_limit: parseInt(e.target.value) || 1000}})}
                min={100}
                max={100000}
              />
              <p className="text-[10px] text-gray-400">Applied to all queries</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Max LIMIT</Label>
              <Input
                type="number"
                value={rules.global.max_limit}
                onChange={e => setRules({...rules, global: {...rules.global, max_limit: parseInt(e.target.value) || 10000}})}
                min={1000}
                max={1000000}
              />
              <p className="text-[10px] text-gray-400">Never exceed this</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Date Filter Days</Label>
              <Input
                type="number"
                value={rules.global.require_date_filter_days}
                onChange={e => setRules({...rules, global: {...rules.global, require_date_filter_days: parseInt(e.target.value) || 90}})}
                min={7}
                max={365}
              />
              <p className="text-[10px] text-gray-400">Max date range without warning</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Peak Hour Limit</Label>
              <Input
                type="number"
                value={rules.global.peak_hour_limit}
                onChange={e => setRules({...rules, global: {...rules.global, peak_hour_limit: parseInt(e.target.value) || 500}})}
                min={100}
                max={10000}
              />
              <p className="text-[10px] text-gray-400">Lower limit during peak hours</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-red-500" />
              Forbidden Operations
            </Label>
            <div className="flex flex-wrap gap-2">
              {['DELETE', 'UPDATE', 'DROP', 'TRUNCATE', 'ALTER', 'INSERT'].map(op => (
                <label key={op} className="flex items-center gap-1 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rules.global.forbidden_operations?.includes(op)}
                    onChange={e => {
                      const ops = rules.global.forbidden_operations || [];
                      setRules({
                        ...rules,
                        global: {
                          ...rules.global,
                          forbidden_operations: e.target.checked 
                            ? [...ops, op]
                            : ops.filter(o => o !== op)
                        }
                      });
                    }}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className={rules.global.forbidden_operations?.includes(op) ? 'text-red-600 font-medium' : 'text-gray-500'}>
                    {op}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1">
                <Clock className="w-3 h-3" /> Peak Hours Start
              </Label>
              <Select 
                value={String(rules.global.peak_hours?.start || 9)}
                onValueChange={v => setRules({...rules, global: {...rules.global, peak_hours: {...rules.global.peak_hours, start: parseInt(v)}}})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({length: 24}, (_, i) => (
                    <SelectItem key={i} value={String(i)}>{i}:00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Peak Hours End</Label>
              <Select 
                value={String(rules.global.peak_hours?.end || 18)}
                onValueChange={v => setRules({...rules, global: {...rules.global, peak_hours: {...rules.global.peak_hours, end: parseInt(v)}}})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({length: 24}, (_, i) => (
                    <SelectItem key={i} value={String(i)}>{i}:00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Timezone</Label>
              <Select 
                value={rules.global.peak_hours?.timezone || 'UTC'}
                onValueChange={v => setRules({...rules, global: {...rules.global, peak_hours: {...rules.global.peak_hours, timezone: v}}})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="UTC">UTC</SelectItem>
                  <SelectItem value="EST">EST</SelectItem>
                  <SelectItem value="PST">PST</SelectItem>
                  <SelectItem value="IST">IST</SelectItem>
                  <SelectItem value="GMT">GMT</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Table-Specific Rules */}
        <div className="p-4 border rounded-lg space-y-4">
          <h4 className="font-medium text-gray-700 flex items-center gap-2">
            <Database className="w-4 h-4 text-green-500" />
            Table-Specific Rules ({rules.tables?.length || 0})
          </h4>
          
          {/* Existing Table Rules */}
          {rules.tables?.length > 0 && (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {rules.tables.map((rule) => (
                <div key={rule.id} className="p-3 bg-gray-50 rounded border flex items-center justify-between">
                  <div className="text-sm">
                    <span className="font-medium">{rule.table}</span>
                    {rule.always_require_date_filter && (
                      <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                        Requires date filter
                      </span>
                    )}
                    {rule.has_partitioning && (
                      <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                        Partitioned by {rule.partition_column}
                      </span>
                    )}
                    {rule.notes && (
                      <p className="text-xs text-gray-400 mt-1">{rule.notes}</p>
                    )}
                  </div>
                  {isAdmin && (
                    <Button size="sm" variant="ghost" onClick={() => handleRemoveTableRule(rule.id)}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add New Table Rule */}
          {isAdmin && (
            <div className="p-3 bg-blue-50 rounded border border-blue-200 space-y-3">
              <Label className="text-xs text-blue-700">Add Table-Specific Rule</Label>
              
              <div className="grid grid-cols-3 gap-2">
                <Input
                  value={newTableRule.server}
                  onChange={e => setNewTableRule({...newTableRule, server: e.target.value})}
                  placeholder="Server name"
                />
                <Input
                  value={newTableRule.database}
                  onChange={e => setNewTableRule({...newTableRule, database: e.target.value})}
                  placeholder="Database"
                />
                <Input
                  value={newTableRule.table}
                  onChange={e => setNewTableRule({...newTableRule, table: e.target.value})}
                  placeholder="Table name *"
                />
              </div>

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newTableRule.always_require_date_filter}
                    onChange={e => setNewTableRule({...newTableRule, always_require_date_filter: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm">Always require date filter</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newTableRule.has_partitioning}
                    onChange={e => setNewTableRule({...newTableRule, has_partitioning: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm">Table is partitioned</span>
                </label>
              </div>

              {newTableRule.always_require_date_filter && (
                <Input
                  value={newTableRule.date_column}
                  onChange={e => setNewTableRule({...newTableRule, date_column: e.target.value})}
                  placeholder="Date column name (e.g., created_at)"
                />
              )}

              {newTableRule.has_partitioning && (
                <Input
                  value={newTableRule.partition_column}
                  onChange={e => setNewTableRule({...newTableRule, partition_column: e.target.value})}
                  placeholder="Partition column (e.g., created_date)"
                />
              )}

              <Textarea
                value={newTableRule.notes}
                onChange={e => setNewTableRule({...newTableRule, notes: e.target.value})}
                placeholder="Notes (e.g., 'This table has 500M+ rows, always use date range')"
                rows={2}
              />

              <Button size="sm" onClick={handleAddTableRule}>
                <Plus className="w-4 h-4 mr-1" /> Add Rule
              </Button>
            </div>
          )}
        </div>

        {/* Save Button */}
        {isAdmin && (
          <div className="flex justify-end">
            <Button onClick={handleSaveGlobalRules} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save All Query Rules
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Phase5QueryRules;
